import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUpload } from "@/lib/useUpload";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { setLanguage } from "@/i18n";
import { LANGUAGES } from "@/i18n/languages";
import { toast } from "sonner";
import { Clock, Upload, FileText, X, CheckCircle2, Loader2, Plus, MessageSquarePlus, Link2, Scale, Handshake, MessageCircleWarning, BookCheck, Ear, Target, ShieldX, Globe } from "lucide-react";

export function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="border-t border-border py-8 mt-12 text-center text-sm text-muted-foreground" data-testid="site-footer">
      <p>© {new Date().getFullYear()} Deliber.Act — {t("civic deliberation that drives action.")}</p>
      <p className="mt-1" data-testid="footer-credit">{t("Deliber.Act is developed by Bruno Paolino Cercal")}</p>
    </footer>
  );
}

export function LanguageSwitcher({ className = "" }) {
  const { i18n } = useTranslation();
  const change = (e) => { localStorage.setItem("lang_manual", "1"); setLanguage(e.target.value); };
  return (
    <div className={`relative inline-flex items-center ${className}`}>
      <Globe size={15} className="absolute left-2 text-muted-foreground pointer-events-none" />
      <select value={i18n.language} onChange={change} data-testid="language-switcher" aria-label="Language" className="appearance-none text-sm border border-border rounded-sm pl-7 pr-3 py-1.5 bg-white text-foreground hover:border-primary outline-none cursor-pointer">
        {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
      </select>
    </div>
  );
}


export const DISCUSSION_RULES = [
  { icon: Handshake, title: "Assume good faith", text: "Treat every participant as a sincere partner working toward the common good." },
  { icon: Scale, title: "Attack ideas, not people", text: "Challenge arguments and proposals — never insult, label or belittle the person." },
  { icon: BookCheck, title: "Cite your sources", text: "Back every factual claim with a verifiable, linked source so others can check it." },
  { icon: Target, title: "Stay on topic", text: "Keep contributions relevant to the debate question and its policy area." },
  { icon: Ear, title: "Listen to understand", text: "Engage with the strongest version of opposing views before you respond." },
  { icon: ShieldX, title: "No harassment or hate", text: "Threats, discrimination, slurs and bad-faith provocation are never tolerated." },
  { icon: MessageCircleWarning, title: "Seek common ground", text: "The goal is a shared, workable proposal — not winning the argument." },
];

export function RulesList({ compact = false }) {
  const { t } = useTranslation();
  return (
    <ul className="space-y-3" data-testid="rules-list">
      {DISCUSSION_RULES.map((r, i) => (
        <li key={i} className="flex items-start gap-3" data-testid={`rule-${i}`}>
          <span className="shrink-0 mt-0.5 w-7 h-7 rounded-sm bg-primary/10 text-primary flex items-center justify-center"><r.icon size={15} /></span>
          <div>
            <p className="text-sm font-semibold text-foreground">{t(r.title)}</p>
            {!compact && <p className="text-sm text-muted-foreground leading-snug">{t(r.text)}</p>}
          </div>
        </li>
      ))}
    </ul>
  );
}

export function RulesPanel() {
  const { t } = useTranslation();
  return (
    <div className="border border-border bg-white p-6 rounded-sm" data-testid="rules-panel">
      <div className="flex items-center gap-2 mb-1"><Scale size={18} className="text-secondary" /><h2 className="font-display text-lg font-bold tracking-tight">{t("Rules of discussion")}</h2></div>
      <p className="text-sm text-muted-foreground mb-4">{t("Every debate on Deliber.Act follows these principles of respectful deliberation.")}</p>
      <RulesList />
      <p className="text-xs text-amber-900 bg-amber-50 border border-amber-300 rounded-sm p-3 mt-4">{t("Behaviour reviews below an average of 5 escalate: warning → next-round suspension → 30-day suspension → permanent ban.")}</p>
    </div>
  );
}

export function RulesModal({ open, onClose }) {
  const { t } = useTranslation();
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/50 backdrop-blur-sm" data-testid="rules-modal" onClick={onClose}>
      <div className="bg-white max-w-lg w-full rounded-sm shadow-xl border border-border max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border p-5 sticky top-0 bg-white">
          <div className="flex items-center gap-2"><Scale size={18} className="text-secondary" /><h2 className="font-display text-lg font-bold tracking-tight">{t("Rules of discussion")}</h2></div>
          <button onClick={onClose} data-testid="rules-modal-close" className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <div className="p-5">
          <p className="text-sm text-muted-foreground mb-4">{t("Before you take part, please keep these principles of respectful deliberation in mind.")}</p>
          <RulesList />
          <p className="text-xs text-amber-900 bg-amber-50 border border-amber-300 rounded-sm p-3 mt-4">{t("Behaviour reviews below an average of 5 escalate: warning → next-round suspension → 30-day suspension → permanent ban.")}</p>
          <button onClick={onClose} data-testid="rules-modal-accept" className="w-full mt-5 bg-primary text-white py-2.5 rounded-sm text-sm font-semibold hover:bg-primary/90 transition-colors">{t("I understand — enter the debate")}</button>
        </div>
      </div>
    </div>
  );
}

export function Countdown({ endsAt }) {
  const { t } = useTranslation();
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const tm = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(tm); }, []);
  const diff = new Date(endsAt).getTime() - now;
  if (isNaN(diff)) return null;
  const over = diff <= 0;
  const d = Math.floor(diff / 86400000), h = Math.floor((diff % 86400000) / 3600000), m = Math.floor((diff % 3600000) / 60000), s = Math.floor((diff % 60000) / 1000);
  return (
    <span data-testid="round-countdown" className={`inline-flex items-center gap-1.5 text-sm font-mono font-semibold ${over ? "text-destructive" : "text-foreground"}`}>
      <Clock size={14} /> {over ? t("Round ended") : `${d}d ${h}h ${m}m ${s}s`}
    </span>
  );
}

const LIFECYCLE = [
  ["debate_creation", "Created"], ["expert_deliberation", "Expert deliberation"], ["consensus_achieved", "Consensus"],
  ["public_vote", "Public vote"], ["public_approval", "Public approval"], ["sent_to_representatives", "Sent to reps"],
  ["representative_endorsement", "Endorsed"], ["submitted_to_legislature", "In legislature"], ["under_legislative_review", "Under review"],
  ["approved", "Approved"], ["implemented", "Implemented"],
];
const SPACE_LIFECYCLE = [
  ["debate_creation", "Created"], ["expert_deliberation", "Expert deliberation"], ["consensus_achieved", "Consensus"],
  ["public_vote", "Public vote"], ["public_approval", "Public approval"], ["approved_by_organiser", "Approved by Organiser"],
];
export function LifecycleBar({ status, space = false }) {
  const { t } = useTranslation();
  const stages = space ? SPACE_LIFECYCLE : LIFECYCLE;
  let idx = stages.findIndex(([k]) => k === status);
  // Map any jurisdiction-only status that may appear on a space debate back onto the space track.
  if (space && idx === -1) {
    const map = { sent_to_representatives: "public_approval", rejected: "public_approval",
      representative_endorsement: "approved_by_organiser", submitted_to_legislature: "approved_by_organiser",
      under_legislative_review: "approved_by_organiser", approved: "approved_by_organiser", implemented: "approved_by_organiser" };
    idx = stages.findIndex(([k]) => k === map[status]);
  }
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="lifecycle-bar">
      {stages.map(([k, label], i) => (
        <span key={k} className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm font-semibold ${i <= idx ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`}>{t(label)}</span>
      ))}
    </div>
  );
}

export function FileUploader({ onUploaded, label }) {
  const { t } = useTranslation();
  const { upload, busy } = useUpload();
  const [files, setFiles] = useState([]);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const rec = await upload(f);
    if (rec) { setFiles((x) => [...x, rec]); onUploaded(rec); }
    e.target.value = "";
  };
  return (
    <div>
      <label className="inline-flex items-center gap-1.5 text-xs border border-border px-3 py-1.5 rounded-sm cursor-pointer hover:bg-muted transition-colors" data-testid="file-uploader">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {label || t("Attach PDF / DOCX / TXT")}
        <input type="file" accept=".pdf,.docx,.txt" className="hidden" onChange={handle} disabled={busy} />
      </label>
      {files.map((f) => (
        <div key={f.id} className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1"><CheckCircle2 size={12} className="text-emerald-600" /> {f.original_filename}</div>
      ))}
    </div>
  );
}

export function FileLinks({ ids }) {
  const [recs, setRecs] = useState([]);
  // ids may be objects {id, name} or strings; we only have ids -> link directly
  useEffect(() => { setRecs(ids || []); }, [ids]);
  if (!ids || ids.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-1">
      {ids.map((fid) => (
        <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary underline"><FileText size={12} /> document</a>
      ))}
    </div>
  );
}

export function NuanceThread({ nuances = [], onAdd, testid = "nuance" }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [source, setSource] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!text.trim()) return toast.error(t("Add your nuance"));
    if (!source.trim()) return toast.error(t("A nuance requires a source link"));
    setBusy(true);
    try { await onAdd(text.trim(), [source.trim()]); setText(""); setSource(""); setOpen(false); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-4 pt-4 border-t border-border" data-testid={`${testid}-thread`}>
      <div className="flex items-center justify-between">
        <p className="overline">{t("Nuance & context")} ({nuances.length})</p>
        <button onClick={() => setOpen(!open)} data-testid={`${testid}-toggle`} className="text-xs inline-flex items-center gap-1 text-primary font-semibold"><MessageSquarePlus size={13} /> {t("Add nuance")}</button>
      </div>
      {nuances.length > 0 && (
        <div className="space-y-2 mt-2">
          {nuances.map((n) => (
            <div key={n.id} className="bg-muted/50 border-l-2 border-secondary p-3 rounded-sm" data-testid={`${testid}-item`}>
              <p className="text-sm">{n.text}</p>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <span className="text-xs text-muted-foreground">{n.author_name}</span>
                {(n.sources || []).map((s, i) => <a key={i} href={s} target="_blank" rel="noreferrer" className="text-xs text-primary underline inline-flex items-center gap-0.5"><Link2 size={10} /> {t("source")} {i + 1}</a>)}
              </div>
            </div>
          ))}
        </div>
      )}
      {open && (
        <div className="mt-3 space-y-2 bg-white border border-border p-3 rounded-sm">
          <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={t("Add nuance or context…")} className="w-full border border-border rounded-sm px-3 py-2 text-sm h-20 outline-none focus:ring-2 focus:ring-primary/50" data-testid={`${testid}-text`} />
          <input value={source} onChange={(e) => setSource(e.target.value)} placeholder={t("Source link (required)")} className="w-full border border-border rounded-sm px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/50" data-testid={`${testid}-source`} />
          <button onClick={submit} disabled={busy} data-testid={`${testid}-submit`} className="text-xs bg-primary text-white px-3 py-1.5 rounded-sm font-semibold disabled:opacity-60">{t("Post nuance")}</button>
        </div>
      )}
    </div>
  );
}

export function Badge({ children, tone = "muted" }) {
  const tones = { muted: "bg-muted text-muted-foreground", primary: "bg-primary text-white", green: "bg-emerald-100 text-emerald-800", amber: "bg-amber-100 text-amber-800", red: "bg-red-100 text-red-800", blue: "bg-blue-100 text-blue-800", purple: "bg-purple-100 text-purple-800" };
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-semibold ${tones[tone]}`}>{children}</span>;
}

export function TopicMultiSelect({ options = [], value = [], onChange, testid = "topics" }) {
  const { t } = useTranslation();
  const toggle = (o) => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => {
        const on = value.includes(o);
        return (
          <button type="button" key={o} onClick={() => toggle(o)} data-testid={`${testid}-${o}`}
            className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${on ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:border-primary"}`}>
            {t(o)}
          </button>
        );
      })}
      {options.length === 0 && <p className="text-xs text-muted-foreground">{t("No topics available.")}</p>}
    </div>
  );
}

export function ExpertiseEditor({ options = [], items, setItems, testid = "expertise" }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState({ area: "", description: "", evidence_links: "", evidence_files: [] });
  const eic = "w-full border border-border bg-background rounded-sm px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary";
  const add = () => {
    if (!draft.area) { toast.error(t("Select an expertise topic")); return; }
    if (!draft.evidence_links.trim() && draft.evidence_files.length === 0) { toast.error(t("Add at least one piece of supporting evidence (link or document)")); return; }
    setItems([...items, draft]);
    setDraft({ area: "", description: "", evidence_links: "", evidence_files: [] });
  };
  return (
    <div className="border border-border rounded-sm p-4 bg-muted/40 space-y-3" data-testid={`${testid}-section`}>
      <p className="overline">{t("Expertise (optional — backed by evidence)")}</p>
      {items.length > 0 && (
        <div className="space-y-2">
          {items.map((x, i) => (
            <div key={i} className="flex items-start justify-between gap-2 bg-white border border-border rounded-sm p-3" data-testid={`${testid}-added`}>
              <div>
                <p className="text-sm font-semibold">{t(x.area)} <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-sm ml-1">{t("pending")}</span></p>
                {x.description && <p className="text-xs text-muted-foreground">{x.description}</p>}
                <p className="text-xs text-muted-foreground">{[x.evidence_links, ...x.evidence_files.map((f) => f.original_filename), (x.evidence_file_ids || []).length ? t("{{count}} attached file(s)", { count: x.evidence_file_ids.length }) : ""].filter(Boolean).join(" · ")}</p>
              </div>
              <button type="button" onClick={() => setItems(items.filter((_, idx) => idx !== i))} data-testid={`${testid}-remove-${i}`} className="text-muted-foreground hover:text-destructive text-xs">{t("Remove")}</button>
            </div>
          ))}
        </div>
      )}
      <div className="space-y-2 border-t border-border pt-3">
        <select value={draft.area} onChange={(e) => setDraft({ ...draft, area: e.target.value })} className={eic} data-testid={`${testid}-area`}>
          <option value="">{t("Select an expertise topic")}</option>
          {options.map((o) => <option key={o} value={o}>{t(o)}</option>)}
        </select>
        <textarea placeholder={t("Description (optional)")} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className={eic + " h-16"} data-testid={`${testid}-desc`} />
        <input placeholder={t("Supporting links (comma): paper, CV, certification, profile")} value={draft.evidence_links} onChange={(e) => setDraft({ ...draft, evidence_links: e.target.value })} className={eic} data-testid={`${testid}-links`} />
        <FileUploader onUploaded={(rec) => setDraft((x) => ({ ...x, evidence_files: [...x.evidence_files, rec] }))} label={t("Upload evidence (PDF/DOCX/TXT)")} />
        <button type="button" onClick={add} data-testid={`${testid}-add-btn`} className="text-xs bg-primary text-white px-3 py-1.5 rounded-sm font-semibold">{t("+ Add expertise area")}</button>
      </div>
    </div>
  );
}

export function expertisePayload(items) {
  return items.map((x) => ({
    area: x.area,
    description: x.description,
    evidence_links: (x.evidence_links || "").split(",").map((s) => s.trim()).filter(Boolean),
    evidence_files: [...(x.evidence_file_ids || []), ...x.evidence_files.map((r) => r.id)],
  }));
}

export function toEditableExpertise(expertise) {
  return (expertise || []).map((e) => ({
    area: e.area, description: e.description || "",
    evidence_links: (e.evidence_links || []).join(", "),
    evidence_files: [], evidence_file_ids: e.evidence_files || [], status: e.status,
  }));
}


export function SpaceJoinForm({ slug, topics = [], organiserToken, onJoined }) {
  const { t } = useTranslation();
  const [interests, setInterests] = useState([]);
  const [expertise, setExpertise] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (interests.length === 0 && expertise.length === 0) { toast.error(t("Select at least one topic of interest or add an expertise area for this space")); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/spaces/${slug}/join`, {
        organiser_token: organiserToken || undefined,
        topics_interest: interests,
        expertise: expertisePayload(expertise),
      });
      toast.success(data.as_organiser ? t("You joined as an organiser") : t("You joined the space"));
      onJoined && onJoined(data);
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="border border-border bg-white p-6 space-y-4 text-left" data-testid="space-join-form">
      <div>
        <p className="overline mb-2">{t("Your topics of interest in this space")}</p>
        <p className="text-xs text-muted-foreground mb-2">{t("Choose from the topics defined by the space organiser. You'll be invited to debates that match these.")}</p>
        <TopicMultiSelect options={topics} value={interests} onChange={setInterests} testid="space-join-topics" />
      </div>
      <ExpertiseEditor options={topics} items={expertise} setItems={setExpertise} testid="space-join-expertise" />
      <button disabled={busy} data-testid="space-join-submit" className="bg-primary text-white px-5 py-2.5 rounded-sm text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60">{busy ? t("Joining…") : (organiserToken ? t("Join as organiser") : t("Join space"))}</button>
    </form>
  );
}

